import './i18n/autoTranslate';
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowRight,
  ArrowLeft,
  ShieldCheck,
  Sparkles,
  Zap,
  Upload,
  FileText,
  X,
  CheckCircle,
  AlertCircle,
  Phone,
  MapPin,
  Globe,
  Briefcase,
  Target,
  Timer,
  BarChart3,
  Wand2,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import AppBackground from "../components/ui/AppBackground.tsx";
import SpotlightCard from "../components/ui/SpotlightCard";
import { Link, useNavigate } from "react-router-dom";

/**
 * Follow-up onboarding page (after signup):
 * - Fill phone_number, country
 * - Upload resume => extract & build resume_data JSON
 * - Add goals (stored inside resume_data.optional)
 * - Save to backend (tries PUT /api/users/me by default) or calls onComplete(payload)
 */

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:5000"; // your auth/api server
const EXTRACT_API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "https://ai-interview-back-node.onrender.com"; // resume extraction service

/* ----------------------------- UI Primitives ----------------------------- */

const Glass = ({ children, className = "" }) => (
  <div
    className={[
      "rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-2xl",
      "shadow-[0_18px_55px_-28px_rgba(0,0,0,0.78)]",
      className,
    ].join(" ")}
  >
    {children}
  </div>
);

const Pill = ({ icon: Icon, tone = "neutral", children }) => {
  const cls =
    tone === "purple"
      ? "border-[#6C63FF]/25 bg-[#6C63FF]/12 text-[#E7E6FF]"
      : tone === "blue"
      ? "border-sky-400/20 bg-sky-400/10 text-sky-100"
      : tone === "emerald"
      ? "border-emerald-400/20 bg-emerald-400/10 text-emerald-100"
      : "border-white/12 bg-white/6 text-white/80";

  return (
    <span
      className={[
        "inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-[11px] font-semibold tracking-wide",
        cls,
      ].join(" ")}
    >
      {Icon ? <Icon className="h-3.5 w-3.5" /> : null}
      {children}
    </span>
  );
};

const PrimaryButton = ({ children, disabled, className = "", ...props }) => (
  <button
    disabled={disabled}
    className={[
      "relative overflow-hidden rounded-2xl px-5 py-3 text-sm font-semibold text-white",
      "bg-gradient-to-br from-[#8B7CFF] to-[#6C63FF]",
      "shadow-[0_18px_55px_-28px_rgba(108,99,255,0.75)]",
      "hover:shadow-[0_0_55px_rgba(108,99,255,0.35)] transition-all",
      "disabled:opacity-50 disabled:cursor-not-allowed",
      className,
    ].join(" ")}
    {...props}
  >
    <span className="relative z-10 inline-flex items-center gap-2">{children}</span>
    <span className="pointer-events-none absolute -bottom-2 left-[18%] h-6 w-1/2 rounded-full bg-white/70 blur-[14px]" />
  </button>
);

const GhostButton = ({ children, disabled, className = "", ...props }) => (
  <button
    disabled={disabled}
    className={[
      "rounded-2xl px-5 py-3 text-sm font-semibold",
      "border border-white/12 bg-white/5 hover:bg-white/8 text-white/85 transition",
      "disabled:opacity-50 disabled:cursor-not-allowed",
      className,
    ].join(" ")}
    {...props}
  >
    <span className="inline-flex items-center gap-2">{children}</span>
  </button>
);

const InputShell = ({ icon: Icon, label, hint, error, children }) => (
  <div className="space-y-2">
    <div className="flex items-end justify-between">
      <label className="text-sm font-medium text-white/85 inline-flex items-center gap-2">
        {Icon ? <Icon className="h-4 w-4 text-[#6C63FF]" /> : null}
        {label}
      </label>
      {hint ? <div className="text-[11px] text-white/45">{hint}</div> : null}
    </div>
    <div className="relative">{children}</div>
    {error ? <div className="text-xs text-red-300">{error}</div> : null}
  </div>
);

/* ----------------------------- Helpers ----------------------------- */

const safeJsonClone = (obj) => JSON.parse(JSON.stringify(obj ?? {}));

const buildResumeDataPayload = ({ extractedResumeData, goals }) => {
  const base = safeJsonClone(extractedResumeData || {});
  base.optional = {
    ...(base.optional || {}),
    ...(goals || {}),
  };
  return base;
};

/* ----------------------------- Component ----------------------------- */

export default function OnboardingAdditionalInfo({ onComplete }) {
  const navigate = useNavigate();

  // Steps: 0 contact, 1 resume, 2 goals, 3 review
  const [step, setStep] = useState(0);

  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState("");

  // Remaining fields from user model (besides signup basics)
  const [phoneNumber, setPhoneNumber] = useState("");
  const [country, setCountry] = useState("");

  // Resume uploader logic (copied + adapted from ResumeUploader.jsx)
  const [file, setFile] = useState(null);
  const [resumeLoading, setResumeLoading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [alertInfo, setAlertInfo] = useState({
    show: false,
    variant: "default",
    title: "",
    description: "",
  });

  // Extracted resume structure: { personalInfo, workExperience, education, skills, optional }
  const [resumeData, setResumeData] = useState(null);

  // Goals (stored inside resume_data.optional)
  const [goals, setGoals] = useState({
    targetRole: "",
    seniority: "intermediate", // junior|intermediate|senior
    interviewFocus: "mixed", // technical|behavioral|mixed
    sessionsPerWeek: 3,
    deadline: "", // optional date string
  });

  const steps = useMemo(
    () => [
      { title: "Contact", desc: "Add the essentials", icon: Phone },
      { title: "Resume", desc: "Extract your profile", icon: FileText },
      { title: "Goals", desc: "Personalize your AI", icon: Target },
      { title: "Review", desc: "Confirm & finish", icon: CheckCircle },
    ],
    []
  );

  const progressPct = useMemo(() => Math.round(((step + 1) / steps.length) * 100), [step, steps.length]);

  const showAlert = (variant, title, description) => {
    setAlertInfo({ show: true, variant, title, description });
    window.clearTimeout(showAlert._t);
    showAlert._t = window.setTimeout(() => {
      setAlertInfo((prev) => ({ ...prev, show: false }));
    }, 4500);
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) setFile(e.target.files[0]);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) setFile(e.dataTransfer.files[0]);
  };

  const removeFile = () => setFile(null);

  const getFileIcon = (fileName) => {
    if (fileName?.toLowerCase()?.endsWith(".pdf")) return <FileText className="h-5 w-5 text-red-400" />;
    if (fileName?.toLowerCase()?.endsWith(".docx")) return <FileText className="h-5 w-5 text-blue-400" />;
    return <FileText className="h-5 w-5 text-white/55" />;
  };

  const getFileType = (fileName) => {
    if (fileName?.toLowerCase()?.endsWith(".pdf")) return "PDF";
    if (fileName?.toLowerCase()?.endsWith(".docx")) return "DOCX";
    return "Document";
  };

  const handleUpload = async () => {
    if (!file) return;
    setResumeLoading(true);
    showAlert("default", "Processing Resume", "Extracting information from your document…");

    try {
      const formData = new FormData();
      formData.append("resume", file);

      const response = await fetch(`${EXTRACT_API_BASE_URL}/extract-text`, {
        method: "POST",
        body: formData,
      });

      const result = await response.json();

      if (result.success) {
        const extractedData = result.extractedData;

        const extractedPersonalInfo = extractedData?.personalInfo || {};
        const fullName = String(extractedPersonalInfo?.name || "").trim();
        const [firstNameFromFull, ...restName] = fullName ? fullName.split(/\s+/) : [];
        const lastNameFromFull = restName.join(" ").trim();
        const firstName = extractedPersonalInfo?.firstName || firstNameFromFull || "";
        const lastName = extractedPersonalInfo?.lastName || lastNameFromFull || "";

        const formattedData = {
          personalInfo: {
            firstName,
            lastName,
            email: extractedPersonalInfo?.email || "",
            phone: extractedPersonalInfo?.phone || "",
            address: extractedPersonalInfo?.address || "",
          },
          workExperience:
            extractedData.workExperience?.map((exp) => ({
              id: Date.now() + Math.random(),
              title: exp.title || "",
              company: exp.company || "",
              description: exp.description || "",
              startDate: exp.startDate || "",
              endDate: exp.endDate || "",
            })) || [],
          education:
            extractedData.education?.map((edu) => ({
              id: Date.now() + Math.random(),
              degree: edu.degree || "",
              institution: edu.institution || "",
              startDate: edu.startDate || "",
              endDate: edu.endDate || "",
            })) || [],
          skills: extractedData.skills || [],
          optional: {},
        };

        setResumeData(formattedData);

        // smart fill phone if empty
        if (!phoneNumber && formattedData?.personalInfo?.phone) setPhoneNumber(formattedData.personalInfo.phone);

        showAlert("default", "Success!", "Resume extracted successfully — ready to personalize.");
      } else {
        console.error("Extraction failed:", result.error);
        showAlert("destructive", "Extraction Failed", "Couldn’t extract. You can continue manually (no problem).");
        setResumeData({
          personalInfo: { firstName: "", lastName: "", email: "", phone: "", address: "" },
          workExperience: [],
          education: [],
          skills: [],
          optional: {},
        });
      }
    } catch (error) {
      console.error("Error uploading resume:", error);
      setResumeData({
        personalInfo: { firstName: "", lastName: "", email: "", phone: "", address: "" },
        workExperience: [],
        education: [],
        skills: [],
        optional: {},
      });
      showAlert("destructive", "Connection Error", "Resume service unreachable. Continue manually (still premium).");
    } finally {
      setResumeLoading(false);
    }
  };

  const validateStep = () => {
    setSubmitError("");
    if (step === 0) {
      if (!phoneNumber?.trim()) return "Phone number is required to secure your account & session reminders.";
      if (!country?.trim()) return "Country is required to localize your interview standards.";
    }
    if (step === 2) {
      if (!goals.targetRole?.trim()) return "Target role is required (this shapes your AI interviews).";
      if (!["junior", "intermediate", "senior"].includes(goals.seniority)) return "Invalid seniority value.";
      if (!["technical", "behavioral", "mixed"].includes(goals.interviewFocus)) return "Invalid interview focus value.";
    }
    return "";
  };

  const next = () => {
    const err = validateStep();
    if (err) {
      setSubmitError(err);
      showAlert("destructive", "Fix Required", err);
      return;
    }
    setStep((s) => Math.min(s + 1, steps.length - 1));
  };

  const back = () => setStep((s) => Math.max(s - 1, 0));

  const saveAll = async () => {
    const err = validateStep();
    if (err) {
      setSubmitError(err);
      showAlert("destructive", "Fix Required", err);
      return;
    }

    setSaving(true);
    setSubmitError("");

    const payload = {
      phone_number: phoneNumber.trim(),
      country: country.trim(),
      resume_data: buildResumeDataPayload({
        extractedResumeData: resumeData,
        goals,
      }),
    };

    try {
      // If caller wants custom save logic, use it (preferred for your app)
      if (typeof onComplete === "function") {
        await onComplete(payload);
        showAlert("default", "Saved", "Your profile is ready. Redirecting…");
        navigate("/dashboard-home");
        return;
      }

      // Default attempt: update current user
      const token = localStorage.getItem("access_token");
      const res = await fetch(`${API_URL}/api/user/profile`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        const msg = data?.message || data?.error || "Failed to save your profile.";
        throw new Error(msg);
      }
      
      const updatedUser = data?.user;
      
      if (updatedUser) {
        localStorage.setItem("user", JSON.stringify(updatedUser));
      }
      
      showAlert("default", "Profile Completed", "Your onboarding is complete — welcome in.");
      navigate("/dashboard-home", { replace: true });

    } catch (e) {
      const msg = e?.message || "Save failed";
      setSubmitError(msg);
      showAlert("destructive", "Save Failed", msg);
    } finally {
      setSaving(false);
    }
  };

  /* ----------------------------- UI blocks ----------------------------- */

  // IMPORTANT: render helpers are called as functions, not as JSX components.
  // When these helpers were rendered as <ContactStep />, <GoalsStep />, etc.,
  // React treated them as new component types on every parent render because
  // they are declared inside this component. That remounted the inputs on
  // every keystroke and made focus disappear. Calling render helpers returns
  // stable elements for reconciliation, so typing keeps focus.
  const renderStepper = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <Pill icon={Sparkles} tone="purple">
            Premium Onboarding
          </Pill>
          <div className="text-2xl sm:text-3xl font-semibold tracking-tight">
            Complete your profile{" "}
            <span className="text-white/55">to unlock stronger AI interviews.</span>
          </div>
        </div>

        <div className="hidden md:flex items-center gap-2">
          <Pill icon={ShieldCheck} tone="emerald">
            Secure
          </Pill>
          <Pill icon={Zap} tone="blue">
            Fast setup
          </Pill>
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-semibold text-white/80">Setup Progress</div>
          <div className="text-xs text-white/55">{progressPct}%</div>
        </div>

        <div className="h-2 rounded-full bg-white/8 overflow-hidden">
          <motion.div
            className="h-full bg-gradient-to-r from-[#6C63FF] to-[#8B7CFF]"
            initial={{ width: 0 }}
            animate={{ width: `${progressPct}%` }}
            transition={{ duration: 0.45, ease: "easeOut" }}
          />
        </div>

        <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-2">
          {steps.map((s, i) => {
            const Icon = s.icon;
            const active = i === step;
            const done = i < step;
            return (
              <div
                key={s.title}
                className={[
                  "rounded-2xl border px-3 py-2.5 transition",
                  active
                    ? "border-[#6C63FF]/35 bg-[#6C63FF]/10"
                    : done
                    ? "border-emerald-400/20 bg-emerald-400/10"
                    : "border-white/10 bg-white/4",
                ].join(" ")}
              >
                <div className="flex items-center gap-2">
                  <div
                    className={[
                      "h-8 w-8 rounded-2xl flex items-center justify-center border",
                      active
                        ? "border-[#6C63FF]/30 bg-[#6C63FF]/15"
                        : done
                        ? "border-emerald-400/20 bg-emerald-400/12"
                        : "border-white/10 bg-white/6",
                    ].join(" ")}
                  >
                    {done ? <CheckCircle className="h-4 w-4 text-emerald-200" /> : <Icon className="h-4 w-4 text-white/75" />}
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs font-semibold truncate">{s.title}</div>
                    <div className="text-[11px] text-white/50 truncate">{s.desc}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  const renderContactStep = () => (
    <div className="space-y-6">
      <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <div className="text-lg font-semibold">Account essentials</div>
            <div className="text-sm text-white/55">
              This keeps reminders, calendar sessions, and AI interview setup accurate.
            </div>
          </div>
          <Pill icon={ShieldCheck} tone="emerald">
            Encrypted sessions
          </Pill>
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <InputShell
            icon={Phone}
            label="Phone number"
            hint="Used for reminders"
            error={submitError.includes("Phone") ? submitError : ""}
          >
            <input
              id="onboarding-phone-number"
              name="phone_number"
              type="tel"
              autoComplete="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="+212 6xx xx xx xx"
              className={[
                "w-full rounded-2xl px-4 py-3.5",
                "bg-white/[0.04] border border-white/10 text-white placeholder:text-white/35",
                "focus:outline-none focus:ring-2 focus:ring-[#6C63FF]/45 focus:border-transparent transition",
              ].join(" ")}
            />
          </InputShell>

          <InputShell
            icon={MapPin}
            label="Country"
            hint="Localizes hiring standards"
            error={submitError.includes("Country") ? submitError : ""}
          >
            <input
              id="onboarding-country"
              name="country"
              type="text"
              autoComplete="country-name"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              placeholder="Morocco"
              className={[
                "w-full rounded-2xl px-4 py-3.5",
                "bg-white/[0.04] border border-white/10 text-white placeholder:text-white/35",
                "focus:outline-none focus:ring-2 focus:ring-[#6C63FF]/45 focus:border-transparent transition",
              ].join(" ")}
            />
          </InputShell>
        </div>
      </div>

      <Glass className="p-5">
        <div className="flex items-start gap-4">
          <div className="h-11 w-11 rounded-2xl border border-[#6C63FF]/25 bg-[#6C63FF]/12 flex items-center justify-center">
            <Wand2 className="h-5 w-5 text-[#E7E6FF]" />
          </div>
          <div className="space-y-1">
            <div className="font-semibold">Why we ask this</div>
            <div className="text-sm text-white/55 leading-relaxed">
              Your AI interviews adapt by region and format — phone + country improves speech pacing, timezones,
              hiring expectations, and performance analytics.
            </div>
          </div>
        </div>
      </Glass>
    </div>
  );

  const renderResumeStep = () => (
    <div className="space-y-6">
      {/* Alert */}
      {alertInfo.show && (
        <div
          className={[
            "p-4 rounded-2xl border backdrop-blur-xl",
            alertInfo.variant === "destructive"
              ? "bg-red-600/10 border-red-500/30 text-red-200"
              : "bg-[#6C63FF]/10 border-[#6C63FF]/25 text-[#E7E6FF]",
          ].join(" ")}
        >
          <div className="flex items-start gap-3">
            {alertInfo.variant === "destructive" ? (
              <AlertCircle className="h-4 w-4 mt-0.5" />
            ) : (
              <CheckCircle className="h-4 w-4 mt-0.5" />
            )}
            <div>
              <div className="font-semibold">{alertInfo.title}</div>
              <div className="text-sm opacity-90">{alertInfo.description}</div>
            </div>
          </div>
        </div>
      )}

      <SpotlightCard spotlightColor="rgba(108, 99, 255, 0.18)">
        <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.05] via-white/[0.03] to-transparent p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
              <div className="text-lg font-semibold">Upload your resume</div>
              <div className="text-sm text-white/55">
                We extract skills + experience and auto-tune your interview difficulty.
              </div>
            </div>
            <Pill icon={Sparkles} tone="purple">
              AI Extraction
            </Pill>
          </div>

          <div
            className={[
              "mt-6 relative border-2 border-dashed rounded-2xl p-8 text-center transition-all",
              dragActive ? "border-[#6C63FF] bg-[#6C63FF]/10" : "border-white/15 hover:border-white/25",
            ].join(" ")}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <Upload className="mx-auto h-12 w-12 text-white/55 mb-4" />
            <div className="space-y-2">
              <div className="text-base font-semibold text-white/85">
                Drop your resume here, or{" "}
                <label htmlFor="resume-upload" className="text-[#AFAAFF] cursor-pointer hover:text-white transition">
                  browse
                </label>
              </div>
              <div className="text-sm text-white/45">PDF or DOCX • Max 10MB • Processed in real time</div>
            </div>
            <input id="resume-upload" type="file" accept=".pdf,.docx" onChange={handleFileChange} className="hidden" />
          </div>

          {/* File Preview */}
          {file && (
            <div className="mt-5 rounded-2xl border border-white/10 bg-white/5 p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  {getFileIcon(file.name)}
                  <div className="min-w-0">
                    <div className="font-semibold text-white/85 truncate">{file.name}</div>
                    <div className="text-xs text-white/45">
                      {getFileType(file.name)} • {(file.size / 1024 / 1024).toFixed(2)} MB
                    </div>
                  </div>
                </div>
                <button onClick={removeFile} className="p-2 rounded-xl hover:bg-white/8 transition">
                  <X className="h-4 w-4 text-white/60" />
                </button>
              </div>
            </div>
          )}

          <div className="mt-5 flex flex-col sm:flex-row gap-3">
            <PrimaryButton onClick={handleUpload} disabled={!file || resumeLoading}>
              {resumeLoading ? (
                <>
                  <span className="h-4 w-4 rounded-full border-2 border-white/25 border-t-white animate-spin" />
                  Extracting…
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4" />
                  Upload & Extract
                </>
              )}
            </PrimaryButton>

            <GhostButton
              type="button"
              onClick={() => {
                // allow skip, but still continue with empty data
                if (!resumeData) {
                  setResumeData({
                    personalInfo: { firstName: "", lastName: "", email: "", phone: "", address: "" },
                    workExperience: [],
                    education: [],
                    skills: [],
                    optional: {},
                  });
                }
                showAlert("default", "Skipped", "No worries — you can add your resume later from your dashboard.");
              }}
            >
              Continue without resume
              <ArrowRight className="h-4 w-4" />
            </GhostButton>
          </div>

          {/* Extracted Summary */}
          {resumeData && (
            <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-3">
              <div className="rounded-2xl border border-white/10 bg-white/4 p-4">
                <div className="text-xs text-white/45">Skills detected</div>
                <div className="text-2xl font-semibold mt-1">{(resumeData.skills || []).length}</div>
                <div className="text-xs text-white/50 mt-2">
                  {(resumeData.skills || []).slice(0, 6).join(" • ") || "Add skills later"}
                </div>
              </div>

              <div className="rounded-2xl border border-white/10 bg-white/4 p-4">
                <div className="text-xs text-white/45">Experience entries</div>
                <div className="text-2xl font-semibold mt-1">{(resumeData.workExperience || []).length}</div>
                <div className="text-xs text-white/50 mt-2">
                  {(resumeData.workExperience || [])[0]?.title
                    ? `${resumeData.workExperience[0].title} @ ${resumeData.workExperience[0].company || "—"}`
                    : "No entries yet"}
                </div>
              </div>

              <div className="rounded-2xl border border-white/10 bg-white/4 p-4">
                <div className="text-xs text-white/45">Education entries</div>
                <div className="text-2xl font-semibold mt-1">{(resumeData.education || []).length}</div>
                <div className="text-xs text-white/50 mt-2">
                  {(resumeData.education || [])[0]?.degree
                    ? `${resumeData.education[0].degree} • ${resumeData.education[0].institution || "—"}`
                    : "No entries yet"}
                </div>
              </div>
            </div>
          )}
        </div>
      </SpotlightCard>

      <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
        <div className="text-xs text-white/50 text-center">
          <span className="font-semibold text-white/75">Privacy Focused:</span> resume is processed for extraction and stored only
          as structured JSON inside your account.
        </div>
      </div>
    </div>
  );

  const renderGoalsStep = () => (
    <div className="space-y-6">
      <Glass className="p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <div className="text-lg font-semibold">Personalize your AI interviews</div>
            <div className="text-sm text-white/55">
              These settings tune difficulty, question style, and scoring. Stored inside <span className="text-white/80">resume_data.optional</span>.
            </div>
          </div>
          <Pill icon={BarChart3} tone="purple">
            Higher accuracy
          </Pill>
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <InputShell icon={Briefcase} label="Target role" hint="Required" error={submitError.includes("Target role") ? submitError : ""}>
            <input
              id="onboarding-target-role"
              name="targetRole"
              type="text"
              autoComplete="organization-title"
              value={goals.targetRole}
              onChange={(e) => setGoals((g) => ({ ...g, targetRole: e.target.value }))}
              placeholder="Frontend Developer"
              className="w-full rounded-2xl px-4 py-3.5 bg-white/[0.04] border border-white/10 text-white placeholder:text-white/35 focus:outline-none focus:ring-2 focus:ring-[#6C63FF]/45 focus:border-transparent transition"
            />
          </InputShell>

          <InputShell icon={Target} label="Interview focus" hint="Shapes question mix">
            <select
              value={goals.interviewFocus}
              onChange={(e) => setGoals((g) => ({ ...g, interviewFocus: e.target.value }))}
              className="w-full rounded-2xl px-4 py-3.5 bg-white/[0.04] border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-[#6C63FF]/45 focus:border-transparent transition"
            >
              <option value="mixed">Mixed (recommended)</option>
              <option value="technical">Technical</option>
              <option value="behavioral">Behavioral</option>
            </select>
          </InputShell>

          <InputShell icon={Zap} label="Seniority" hint="Controls difficulty baseline">
            <div className="grid grid-cols-3 gap-2">
              {[
                { k: "junior", t: "Junior" },
                { k: "intermediate", t: "Intermediate" },
                { k: "senior", t: "Senior" },
              ].map((x) => {
                const active = goals.seniority === x.k;
                return (
                  <button
                    key={x.k}
                    type="button"
                    onClick={() => setGoals((g) => ({ ...g, seniority: x.k }))}
                    className={[
                      "rounded-2xl px-3 py-3 text-sm font-semibold border transition",
                      active ? "border-[#6C63FF]/35 bg-[#6C63FF]/12 text-white" : "border-white/10 bg-white/4 text-white/70 hover:bg-white/6",
                    ].join(" ")}
                  >
                    {x.t}
                  </button>
                );
              })}
            </div>
          </InputShell>

          <InputShell icon={Timer} label="Sessions per week" hint="Your training cadence">
            <div className="rounded-2xl border border-white/10 bg-white/4 px-4 py-4">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold text-white/80">{goals.sessionsPerWeek} sessions</div>
                <div className="text-xs text-white/45">Recommended: 3–5</div>
              </div>
              <input
                type="range"
                min={1}
                max={10}
                value={goals.sessionsPerWeek}
                onChange={(e) => setGoals((g) => ({ ...g, sessionsPerWeek: Number(e.target.value) }))}
                className="w-full mt-3 accent-[#6C63FF]"
              />
            </div>
          </InputShell>

          <div className="md:col-span-2">
            <InputShell icon={Globe} label="Goal deadline (optional)" hint="Helps pacing & reminders">
              <input
                id="onboarding-goal-deadline"
                name="deadline"
                type="date"
                value={goals.deadline}
                onChange={(e) => setGoals((g) => ({ ...g, deadline: e.target.value }))}
                placeholder="2026-03-15"
                className="w-full rounded-2xl px-4 py-3.5 bg-white/[0.04] border border-white/10 text-white placeholder:text-white/35 focus:outline-none focus:ring-2 focus:ring-[#6C63FF]/45 focus:border-transparent transition"
              />
            </InputShell>
          </div>
        </div>
      </Glass>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {[
          { icon: Sparkles, title: "Better Questions", desc: "Your role + seniority shape the interviewer persona." },
          { icon: BarChart3, title: "Cleaner Analytics", desc: "Scores become comparable across sessions." },
          { icon: ShieldCheck, title: "Stronger Trust", desc: "Fewer generic prompts. More signal." },
        ].map((b) => (
          <Glass key={b.title} className="p-5">
            <div className="flex items-start gap-4">
              <div className="h-11 w-11 rounded-2xl border border-white/10 bg-white/6 flex items-center justify-center">
                <b.icon className="h-5 w-5 text-white/75" />
              </div>
              <div className="space-y-1">
                <div className="font-semibold">{b.title}</div>
                <div className="text-sm text-white/55 leading-relaxed">{b.desc}</div>
              </div>
            </div>
          </Glass>
        ))}
      </div>
    </div>
  );

  const renderReviewStep = () => {
    const finalResumePayload = buildResumeDataPayload({ extractedResumeData: resumeData, goals });

    const completeness = (() => {
      let score = 0;
      score += phoneNumber?.trim() ? 18 : 0;
      score += country?.trim() ? 18 : 0;
      score += resumeData ? 24 : 0;
      score += goals.targetRole?.trim() ? 20 : 0;
      score += goals.seniority ? 10 : 0;
      score += goals.interviewFocus ? 10 : 0;
      return Math.min(100, score);
    })();

    return (
      <div className="space-y-6">
        <Glass className="p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
              <div className="text-lg font-semibold">Review & finish</div>
              <div className="text-sm text-white/55">
                Confirm your details. This improves your very first AI interview session.
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
              <div className="text-xs text-white/50">Profile readiness</div>
              <div className="text-2xl font-semibold">{completeness}%</div>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-4">
            <div className="lg:col-span-7 space-y-3">
              <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                <div className="text-xs text-white/45 mb-2">Contact</div>
                <div className="text-sm text-white/85">
                  <span className="text-white/55">Phone:</span> {phoneNumber || "—"}{" "}
                  <span className="mx-2 text-white/20">•</span>{" "}
                  <span className="text-white/55">Country:</span> {country || "—"}
                </div>
              </div>

              <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                <div className="text-xs text-white/45 mb-2">Goals</div>
                <div className="text-sm text-white/85">
                  <span className="text-white/55">Role:</span> {goals.targetRole || "—"}{" "}
                  <span className="mx-2 text-white/20">•</span>{" "}
                  <span className="text-white/55">Seniority:</span> {goals.seniority}{" "}
                  <span className="mx-2 text-white/20">•</span>{" "}
                  <span className="text-white/55">Focus:</span> {goals.interviewFocus}
                </div>
                <div className="text-xs text-white/50 mt-2">
                  Sessions/week: <span className="text-white/80 font-semibold">{goals.sessionsPerWeek}</span>
                  {goals.deadline ? (
                    <>
                      <span className="mx-2 text-white/20">•</span> Deadline:{" "}
                      <span className="text-white/80 font-semibold">{goals.deadline}</span>
                    </>
                  ) : null}
                </div>
              </div>

              <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                <div className="text-xs text-white/45 mb-2">Resume JSON snapshot</div>
                <pre className="text-[11px] leading-relaxed text-white/70 max-h-44 overflow-auto">
                  {JSON.stringify(finalResumePayload || {}, null, 2)}
                </pre>
              </div>
            </div>

            <div className="lg:col-span-5 space-y-3">
              <div className="rounded-2xl border border-[#6C63FF]/20 bg-[#6C63FF]/10 p-5">
                <div className="flex items-start gap-4">
                  <div className="h-11 w-11 rounded-2xl border border-[#6C63FF]/25 bg-[#6C63FF]/12 flex items-center justify-center">
                    <Sparkles className="h-5 w-5 text-[#E7E6FF]" />
                  </div>
                  <div className="space-y-1">
                    <div className="font-semibold">What you unlock now</div>
                    <div className="text-sm text-[#E7E6FF]/80">
                      First session starts with personalized questions, calibrated scoring, and faster onboarding to “real interview mode”.
                    </div>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-2">
                  {[
                    "Better prompts",
                    "Cleaner reports",
                    "Faster setup",
                    "Higher trust UI",
                  ].map((x) => (
                    <div key={x} className="rounded-2xl border border-white/10 bg-white/5 px-3 py-2 text-[12px] text-white/80">
                      {x}
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
                <div className="text-sm font-semibold mb-3">Security & privacy</div>
                <div className="space-y-2 text-sm text-white/55 leading-relaxed">
                  <div className="flex items-start gap-2">
                    <ShieldCheck className="h-4 w-4 mt-0.5 text-emerald-200" />
                    Resume data stored as JSON (no raw files needed for daily usage).
                  </div>
                  <div className="flex items-start gap-2">
                    <ShieldCheck className="h-4 w-4 mt-0.5 text-emerald-200" />
                    Session personalization uses your profile — never exposed publicly.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Glass>

        {submitError ? (
          <div className="rounded-2xl border border-red-500/30 bg-red-500/10 p-4 text-red-200 text-sm">
            {submitError}
          </div>
        ) : null}
      </div>
    );
  };

  /* ----------------------------- Layout ----------------------------- */

  return (
    <AppBackground>
      {/* Ambient background glows */}
      {/* <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-24 -left-24 h-[520px] w-[520px] rounded-full bg-[#6C63FF]/12 blur-[90px]" />
        <div className="absolute -bottom-28 -right-24 h-[560px] w-[560px] rounded-full bg-[#8B7CFF]/12 blur-[100px]" />
        <div className="absolute inset-0 opacity-35 [background-image:radial-gradient(rgba(255,255,255,0.08)_1px,transparent_1px)] [background-size:26px_26px]" />
        <div className="absolute inset-0 bg-[radial-gradient(1200px_600px_at_65%_20%,rgba(255,255,255,0.10),transparent_55%)]" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/20 to-black/75" />
      </div> */}

      {/* Top nav */}
      <div className="relative z-10 mt-20">
        <nav className="sticky top-0 z-50 border-b border-white/10 bg-black/20 backdrop-blur-xl">
          <div className="mx-auto max-w-7xl px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              {/* <div className="h-10 w-10 rounded-2xl border border-white/10 bg-white/6 flex items-center justify-center">
                <Sparkles className="h-4.5 w-4.5 text-white/85" />
              </div> */}
              <div className="leading-tight">
                <div className="text-sm font-semibold tracking-tight">Onboarding</div>
                {/* <div className="text-[11px] text-white/55 -mt-0.5"></div> */}
              </div>
            </Link>

            <div className="flex items-center gap-2">
              <Pill icon={ShieldCheck} tone="emerald">
                Protected
              </Pill>
              <Pill icon={Zap} tone="purple">
                Investor-grade UX
              </Pill>
            </div>
          </div>
        </nav>

        {/* Content */}
        <div className="mx-auto max-w-7xl px-6 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
            <div className="lg:col-span-7 space-y-6">
              {renderStepper()}

              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.25, ease: "easeOut" }}
                  className="space-y-6"
                >
                  {step === 0 ? renderContactStep() : null}
                  {step === 1 ? renderResumeStep() : null}
                  {step === 2 ? renderGoalsStep() : null}
                  {step === 3 ? renderReviewStep() : null}
                </motion.div>
              </AnimatePresence>

              {/* Controls */}
              <div className="flex items-center justify-between pt-2">
                <GhostButton onClick={back} disabled={step === 0 || saving}>
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </GhostButton>

                {step < steps.length - 1 ? (
                  <PrimaryButton onClick={next} disabled={saving}>
                    Continue
                    <ArrowRight className="h-4 w-4" />
                  </PrimaryButton>
                ) : (
                  <PrimaryButton onClick={saveAll} disabled={saving}>
                    {saving ? (
                      <>
                        <span className="h-4 w-4 rounded-full border-2 border-white/25 border-t-white animate-spin" />
                        Saving…
                      </>
                    ) : (
                      <>
                        Finish setup
                        <ArrowRight className="h-4 w-4" />
                      </>
                    )}
                  </PrimaryButton>
                )}
              </div>
            </div>

            {/* Right rail: premium “trust” / preview */}
            <div className="lg:col-span-5 space-y-4 lg:sticky lg:top-24">
              <Glass className="p-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="space-y-1">
                    <div className="text-sm font-semibold text-white/80">AI Setup Preview</div>
                    <div className="text-xs text-white/50">
                      Your profile becomes the “interview engine context”.
                    </div>
                  </div>
                  <div className="h-11 w-11 rounded-2xl border border-white/10 bg-white/6 flex items-center justify-center">
                    <Sparkles className="h-5 w-5 text-white/80" />
                  </div>
                </div>

                <div className="mt-5 grid grid-cols-1 gap-3">
                  <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                    <div className="text-xs text-white/45">Target role</div>
                    <div className="text-base font-semibold mt-1">{goals.targetRole || "—"}</div>
                    <div className="text-xs text-white/50 mt-1">Seniority: {goals.seniority}</div>
                  </div>

                  <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                    <div className="text-xs text-white/45">Resume status</div>
                    <div className="text-base font-semibold mt-1">
                      {resumeData ? "Extracted ✅" : "Not added yet"}
                    </div>
                    <div className="text-xs text-white/50 mt-1">
                      Skills: {(resumeData?.skills || []).length} • Experience: {(resumeData?.workExperience || []).length}
                    </div>
                  </div>

                  <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                    <div className="text-xs text-white/45">Weekly cadence</div>
                    <div className="text-base font-semibold mt-1">{goals.sessionsPerWeek} sessions/week</div>
                    <div className="text-xs text-white/50 mt-1">Focus: {goals.interviewFocus}</div>
                  </div>
                </div>
              </Glass>

              <Glass className="p-6">
                <div className="flex items-start gap-4">
                  <div className="h-11 w-11 rounded-2xl border border-[#6C63FF]/25 bg-[#6C63FF]/12 flex items-center justify-center">
                    <ShieldCheck className="h-5 w-5 text-[#E7E6FF]" />
                  </div>
                  <div className="space-y-1">
                    <div className="font-semibold">Production-grade onboarding</div>
                    <div className="text-sm text-white/55 leading-relaxed">
                      This flow is designed to feel like a premium SaaS product — clean, confident, and high trust.
                    </div>
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  <Pill icon={ShieldCheck} tone="emerald">
                    Secure profile
                  </Pill>
                  <Pill icon={Sparkles} tone="purple">
                    Smart defaults
                  </Pill>
                  <Pill icon={Timer} tone="blue">
                    Fast completion
                  </Pill>
                </div>
              </Glass>
            </div>
          </div>
        </div>
      </div>
    </AppBackground>
  );
}
